import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ShieldCheck, Search, XCircle, CheckCircle2, Scale } from "lucide-react";
import { api } from "@/lib/api";

export default function VerifyPublic() {
  const params = useParams();
  const navigate = useNavigate();
  const [code, setCode] = useState(params.token || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const lookup = async (val) => {
    const c = (val ?? code).trim();
    if (!c) return;
    setLoading(true); setError(""); setResult(null);
    try {
      const r = await api.get(`/public/verify/${c}`);
      setResult(r.data);
      navigate(`/verify/${c}`, { replace: true });
    } catch (e) {
      setError(e?.response?.data?.detail || "Não foi possível consultar");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white ring-1 ring-slate-200 flex items-center justify-center overflow-hidden">
            <img src="/logo.png" alt="Del Padre Advocacia" className="w-full h-full object-contain p-0.5"/>
          </div>
          <div>
            <div className="font-heading text-base font-semibold text-slate-900">Hermes · Verificação de autenticidade</div>
            <div className="text-xs text-slate-500">Del Padre Advocacia</div>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-10">
        <h1 className="font-heading text-3xl font-semibold text-slate-900">Verificar documento</h1>
        <p className="text-slate-500 mt-1">Cole o <b>identificador do link</b> (aparece no rodapé do documento assinado) para conferir autenticidade e status.</p>

        <div className="mt-6 bg-white rounded-2xl border border-slate-100 p-5 flex gap-3">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-3.5 text-slate-400"/>
            <input value={code} onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && lookup()}
              placeholder="Cole aqui o identificador"
              className="w-full h-11 pl-9 pr-3 rounded-lg bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-slate-900 outline-none text-sm font-mono"
              data-testid="verify-input"/>
          </div>
          <button onClick={() => lookup()} disabled={loading || !code.trim()} data-testid="btn-verify"
            className="px-5 h-11 rounded-lg gradient-primary text-white text-sm font-medium hover:-translate-y-0.5 transition disabled:opacity-60">
            {loading ? "Consultando..." : "Consultar"}
          </button>
        </div>

        {error && <div className="mt-5 bg-red-50 border border-red-200 text-red-700 text-sm p-4 rounded-xl">{error}</div>}

        {result && !result.found && (
          <div className="mt-6 bg-white rounded-2xl border border-slate-100 p-6 flex items-center gap-3">
            <XCircle className="w-6 h-6 text-red-500"/>
            <div>
              <div className="font-heading text-lg font-semibold text-slate-900">Não localizado</div>
              <div className="text-sm text-slate-500">Nenhum documento com este identificador foi encontrado em nossos registros.</div>
            </div>
          </div>
        )}

        {result?.found && (
          <div className="mt-6 bg-white rounded-2xl border border-slate-100 overflow-hidden">
            <div className={`p-5 flex items-center gap-3 ${result.signed?'bg-emerald-50':'bg-slate-50'}`}>
              {result.signed ? <CheckCircle2 className="w-6 h-6 text-emerald-600"/> : <ShieldCheck className="w-6 h-6 text-slate-500"/>}
              <div>
                <div className="font-heading text-lg font-semibold text-slate-900">
                  {result.signed ? "Documento autêntico e assinado" : "Documento localizado (não assinado)"}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {result.signed
                    ? `Assinado por ${result.signer_name || "—"} em ${new Date(result.signed_at).toLocaleString('pt-BR')}`
                    : `Emitido em ${result.created_at ? new Date(result.created_at).toLocaleString('pt-BR') : "—"}`}
                </div>
              </div>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <Row label="Documento" value={result.title}/>
              <Row label="Tipo" value={result.type?.replace(/_/g," ")}/>
              <Row label="Status" value={{draft:"Rascunho",sent:"Enviado",signed:"Assinado",archived:"Arquivado"}[result.status] || result.status}/>
              <Row label="Assinado por" value={result.signer_name}/>
              <Row label="IP do signatário" value={result.signer_ip}/>
              <Row label="Escritório" value={result.lawyer_escritorio}/>
              <Row label="Advogado responsável" value={result.lawyer_name}/>
              <Row label="OAB" value={result.lawyer_oab}/>
            </div>
            <div className="px-5 py-4 bg-slate-50 border-t border-slate-100 text-xs text-slate-500">
              Esta verificação confirma a existência do documento nos registros do sistema Hermes. Para o teor completo, solicite ao escritório o link de leitura correspondente.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div>
    <div className="text-xs text-slate-500">{label}</div>
    <div className="text-sm text-slate-900 font-medium mt-0.5">{value || "—"}</div>
  </div>
);
